import { msg } from '@lingui/core/macro';
import _ from 'lodash';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Trans, useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GridLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { deriveAsyncState } from 'shared/utils/async-state';
import { AppDispatch } from 'store/store';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDActionButton from 'ud-ui/components/action-button';
import UDButton from 'ud-ui/components/button';
import UDEmptyState from 'ud-ui/components/empty-state';
import { Scheme } from 'modules/overlay-canvas/types';
import history from 'modules/overlay-canvas/addons/history';
import { setHasChanges, setPreviewState } from 'modules/template-editor/store/editor/editor';
import { selectHasChange, selectIsInit, selectPreviewState, selectSavedScheme } from 'modules/template-editor/store/editor/selectors';
import { selectBoardIds, selectFailedTemplates, selectLoadedTemplates } from 'modules/template-editor/store/templates/selectors';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useLocalStorage from 'modules/template-editor/ui/hooks/use-local-storage';
import useSaveScheme from 'modules/template-editor/ui/hooks/use-save-scheme';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import Canvas from './canvas';
import { Button, Container, Header, MainBlock, RightBlock, TemplateName } from './styles';
import { Zoom } from './zoom';
import { theme } from 'styles/theme/theme';
import { useEditorManager } from 'modules/template-editor/ui/context';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

function Content() {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const state = useLocation().state as null | { from: string };
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params = useParams();
  const templateId = Number(params.templateId);
  const boardId = Number(params.boardId);
  const { activeTemplate } = useTemplates();
  const { activeBoard, activeScheme, isProcessed, isLoading: isLoadingBoards } = useBoards();
  const previewScheme = useSelector(selectPreviewState)?.scheme;
  const loadedTemplates = useSelector(selectLoadedTemplates);
  const failedTemplates = useSelector(selectFailedTemplates);
  const boardIds = useSelector(selectBoardIds);
  const hasChange = useSelector(selectHasChange);
  const savedScheme = useSelector(selectSavedScheme);
  const isInit = useSelector(selectIsInit);
  const manager = useEditorManager();
  const { setData, getData } = useLocalStorage();
  const { saveScheme } = useSaveScheme();
  const boardName = useMemo(() => {
    return activeBoard?.name || getData()?.boardName;
  }, [activeBoard?.name, getData]);

  setData({
    scheme: activeScheme,
    boardId: activeBoard?.id,
    resolution: activeTemplate?.resolution,
    boardType: activeBoard?.type,
    from: state?.from,
    templateId: activeTemplate?.id,
    boardName: activeBoard?.name,
  });

  useEffect(() => {
    const historySubscriber = {
      update: (state: Scheme) => {
        if (!manager) return;
        if (!savedScheme) return;
        const isEqual = _.isEqual(state, savedScheme);
        dispatch(setHasChanges(!isEqual));
      },
    };

    history.onChangeState.subscribe(historySubscriber);

    return () => {
      history.onChangeState.unsubscribe(historySubscriber);
    };
  }, [dispatch, manager, savedScheme]);

  // Промис — кнопке «Сохранить»: она заперта до ответа (раньше повторный клик слал второе сохранение).
  const onClickSave = useCallback(() => {
    return saveScheme()
      .then(() => {
        toast.success(t`Табло успешно сохранено`);
      })
      .catch((e) => {
        console.error(e);
        toastApiError(e, msg`Не удалось сохранить`);
      });
  }, [saveScheme, t, toastApiError]);
  const onGoPreview = useCallback(() => {
    if (!manager) {
      console.warn({ event: 'go to preview', message: 'manager not found' });
      return;
    }

    if (!activeTemplate) {
      console.warn({ event: 'go to preview', message: 'template not found', activeTemplate });
      return;
    }

    if (!activeBoard) {
      console.warn({ event: 'go to preview', message: 'board not found', activeBoard });
      return;
    }

    history.disableHistory();
    manager.canvas.discardActiveObject();
    const historyState = history.getHistory();
    const curScheme = manager.getScheme(true);
    dispatch(
      setPreviewState({
        curHistoryIndex: historyState.index,
        history: historyState.history,
        scheme: curScheme,
        resolution: manager.resolution.value,
        backgroundColor: manager.canvas.backgroundColor?.toString(),
        viewport: manager.canvas.viewportTransform ? [...manager.canvas.viewportTransform] : undefined,
      }),
    );

    navigate(Pages.TEMPLATE.builders.preview(activeTemplate.id, activeBoard.id));
  }, [activeBoard, activeTemplate, dispatch, manager, navigate]);

  // Канва грузит схему один раз, при монтировании (`canvas.tsx`), поэтому монтируется только когда
  // схема уже есть: снимок из превью или доска из адреса. По прямой ссылке и после F5 доски ещё
  // в пути — ждём их, а не берём что есть (IMGM-1556).
  const boardState = useMemo(() => {
    const isTemplateSettled = loadedTemplates.includes(templateId) || failedTemplates.includes(templateId);
    // Активная доска ставится из адреса эффектом `useLoadTemplates`, то есть после первого рендера. До
    // этого в сторе может быть активна другая доска (её выбрали в списке, а вернулись «Назад» браузера) —
    // её схему на канву не пускаем, иначе «Сохранить» запишет чужое содержимое в доску из адреса.
    const isUrlBoardActive = activeBoard?.id === boardId;
    const isUrlBoardPending = !isUrlBoardActive && boardIds.includes(boardId);
    const state = deriveAsyncState({
      itemsCount: isUrlBoardActive && (previewScheme || activeScheme) ? 1 : 0,
      hasCurrentData: isTemplateSettled,
      isFetching: isLoadingBoards,
      isPending: isUrlBoardPending,
      // Упавший запрос досок — тоже «не найдено»: тост об ошибке уже показал `useLoadTemplates`.
      isError: false,
    });
    // Доска уже на канве — фоновая загрузка досок её не меняет, приглушать редактор незачем.
    return { ...state, isRefreshing: false };
  }, [activeBoard?.id, activeScheme, boardId, boardIds, failedTemplates, isLoadingBoards, loadedTemplates, previewScheme, templateId]);

  return (
    <MainBlock className={'flex-center h-full w-full flex-col'}>
      <UDAsyncContent
        state={boardState}
        loading={<GridLoader />}
        empty={
          <UDEmptyState
            title={<Trans>Табло не найдено</Trans>}
            subtitle={<Trans>Возможно, его удалили или ссылка устарела.</Trans>}
            action={
              <UDButton variant="primary" type="button" onClick={() => navigate(Pages.TEMPLATE.builders.boards(templateId))}>
                <Trans>К списку табло</Trans>
              </UDButton>
            }
          />
        }
      >
        {!isInit && <GridLoader style={{ position: 'absolute' }} />}
        <div style={{ visibility: isInit ? 'visible' : 'hidden' }}>
          <Header>
            <div>
              <TemplateName>{boardName ?? t`Неизвестное табло`}</TemplateName>
            </div>
            <RightBlock>
              <Button onClick={onGoPreview} color={theme.colors.SurfaceWhite}>
                <Trans>Предпросмотр</Trans>
              </Button>
              {/* Своя кнопка редактора (палитра — известное исключение) с китовым замком `UDActionButton`. */}
              <UDActionButton as={Button} disabled={!hasChange} onClick={onClickSave} color={theme.colors.AccentNaplesYellow}>
                {(isBusy: boolean) => (isProcessed || isBusy ? t`Сохранение` : hasChange ? t`Сохранить` : t`Сохранено`)}
              </UDActionButton>
            </RightBlock>
          </Header>
          <Container>
            <Canvas />
            <Zoom />
          </Container>
        </div>
      </UDAsyncContent>
    </MainBlock>
  );
}

export default Content;
